import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8000/api/v1';

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Try to get token from store first, then localStorage
    const storeToken = useAuthStore.getState().token;
    const localStorageToken = localStorage.getItem('token');
    const token = storeToken || localStorageToken;
    
    console.log('[API] Interceptor - store token:', storeToken ? 'exists' : 'null', 
                'localStorage token:', localStorageToken ? 'exists' : 'null');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('[API] Adding auth token to request:', config.url);
    } else {
      console.warn('[API] No token found for request:', config.url);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear token from both store and localStorage
      console.log('[API] 401 error, clearing auth');
      useAuthStore.getState().clearAuth();
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  register: async (email: string, username: string, password: string) => {
    const response = await api.post('/auth/register', { email, username, password });
    return response.data;
  },
  
  debugLogin: async () => {
    const response = await api.post('/auth/debug-login');
    return response.data;
  },
  
  getDebugStatus: async () => {
    const response = await api.get('/debug/login-status');
    return response.data;
  },
};

// Tweets API
export const tweetsApi = {
  getFeed: async (params: {
    category?: string;
    author?: string;
    keyword?: string;
    time_window_hours?: number;
    tags?: string[];
    page?: number;
    page_size?: number;
    include_translated?: boolean;
  }) => {
    const response = await api.get('/tweets/feed', { params });
    return response.data;
  },
  
  search: async (q: string, params?: { category?: string; page?: number; page_size?: number }) => {
    const response = await api.get('/tweets/search', { params: { q, ...params } });
    return response.data;
  },
  
  bookmark: async (tweetId: number) => {
    const response = await api.post(`/tweets/${tweetId}/bookmark`);
    return response.data;
  },
};

// Settings API
export const settingsApi = {
  getPreferences: async () => {
    const response = await api.get('/settings/preferences');
    return response.data;
  },
  
  updatePreferences: async (data: Partial<import('@/types').UserPreferences>) => {
    const response = await api.put('/settings/preferences', data);
    return response.data;
  },
  
  getLLMProfiles: async () => {
    const response = await api.get('/settings/llm-profiles');
    return response.data;
  },
  
  createLLMProfile: async (data: import('@/types').LLMProfileCreate) => {
    const response = await api.post('/settings/llm-profiles', data);
    return response.data;
  },
  
  updateLLMProfile: async (id: number, data: Partial<import('@/types').LLMProfileCreate>) => {
    const response = await api.put(`/settings/llm-profiles/${id}`, data);
    return response.data;
  },
  
  deleteLLMProfile: async (id: number) => {
    const response = await api.delete(`/settings/llm-profiles/${id}`);
    return response.data;
  },
  
  getUnprocessedCount: async () => {
    const response = await api.get('/settings/unprocessed-count');
    return response.data;
  },
  
  reprocessUnprocessed: async () => {
    const response = await api.post('/settings/reprocess');
    return response.data;
  },
};

// Processing API
export const processingApi = {
  getRuns: async (limit?: number) => {
    const response = await api.get('/processing/runs', { params: { limit } });
    return response.data;
  },
  
  manualRun: async () => {
    const response = await api.post('/processing/runs/manual', {});
    return response.data;
  },
  
  getDigests: async (limit?: number) => {
    const response = await api.get('/processing/digests', { params: { limit } });
    return response.data;
  },
  
  getDigest: async (id: number) => {
    const response = await api.get(`/processing/digests/${id}`);
    return response.data;
  },
  
  getLessons: async (limit?: number) => {
    const response = await api.get('/processing/lessons', { params: { limit } });
    return response.data;
  },
  
  getLesson: async (id: number) => {
    const response = await api.get(`/processing/lessons/${id}`);
    return response.data;
  },
};

// X Accounts API
export const xAccountsApi = {
  getAccounts: async () => {
    const response = await api.get('/x-accounts');
    return response.data;
  },
  
  login: async (username: string, email: string, password: string) => {
    const response = await api.post('/x-accounts/login', { username, email, password });
    return response.data;
  },
  
  delete: async (id: number) => {
    const response = await api.delete(`/x-accounts/${id}`);
    return response.data;
  },
  
  test: async (id: number) => {
    const response = await api.post(`/x-accounts/${id}/test`);
    return response.data;
  },
};

// SSE Connection
export function createSSEConnection(): EventSource {
  // Try store first, then localStorage
  const token = useAuthStore.getState().token || localStorage.getItem('token');
  const sseUrl = `${API_BASE}/feed/sse`;
  
  // Create EventSource with auth header via query param (workaround)
  const url = token ? `${sseUrl}?token=${token}` : sseUrl;
  
  console.log('[SSE] Creating connection, token exists:', !!token);
  
  return new EventSource(url);
}

export default api;
